import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	ConfluenceAggregationDimension,
	useContentPerformanceSummaryQuery$data,
	useContentPerformanceSummaryQuery$variables,
} from './__generated__/useContentPerformanceSummaryQuery.graphql.ts';

const useContentPerformanceSummaryQuery = graphql`
	query useContentPerformanceSummaryQuery(
		$cloudId: ID!
		$startTimestamp: String!
		$endTimestamp: String!
		$spaceId: ID
		$aggregateDimension: ConfluenceAggregationDimension!
	) {
		confluence_contentPerformanceSummary(
			cloudId: $cloudId
			startTimestamp: $startTimestamp
			endTimestamp: $endTimestamp
			spaceId: $spaceId
			aggregateDimension: $aggregateDimension
		) {
			totalContent
			distribution {
				buckets {
					key
					definition {
						min
						max
						unit
					}
					count
					percentage
				}
			}
		}
	}
`;

const VALID_BUCKET_KEYS = ['fast', 'moderate', 'slow'] as const;

type BucketKey = (typeof VALID_BUCKET_KEYS)[number];

type BucketData = {
	count: number;
	percentage: number;
};

type ContentPerformanceSummaryData = Record<BucketKey, BucketData> & {
	total: number;
};

type UseContentPerformanceSummaryArgs = {
	startTimestamp: string;
	endTimestamp: string;
	spaceId?: string;
	aggregateDimension?: ConfluenceAggregationDimension;
};

export type UseContentPerformanceSummaryResult = {
	data: ContentPerformanceSummaryData | null;
	loading: boolean;
	error?: Error;
};

const DEFAULT_BUCKET: BucketData = { count: 0, percentage: 0 };

export const useContentPerformanceSummary = ({
	startTimestamp,
	endTimestamp,
	spaceId,
	aggregateDimension = 'PERFORMANCE_RATING',
}: UseContentPerformanceSummaryArgs): UseContentPerformanceSummaryResult => {
	const { cloudId } = useSessionData();

	const {
		data: rawData,
		loading,
		error,
	} = useQueryInterop<
		useContentPerformanceSummaryQuery$data,
		useContentPerformanceSummaryQuery$variables
	>(useContentPerformanceSummaryQuery, {
		variables: {
			cloudId,
			startTimestamp,
			endTimestamp,
			spaceId,
			aggregateDimension,
		},
		skip: !cloudId || !startTimestamp || !endTimestamp,
	});

	// @todo: Update returned data to also include bucket definitions (e.g. min/max values) if needed for future features like dynamic bucket labels

	const data = useMemo(() => {
		const summary = rawData?.confluence_contentPerformanceSummary;
		if (!summary) {
			return null;
		}

		const bucketMap = new Map<string, BucketData>();
		for (const bucket of summary.distribution.buckets) {
			if (bucket) {
				bucketMap.set(bucket.key, {
					count: bucket.count,
					percentage: bucket.percentage ?? 0,
				});
			}
		}

		const result = {} as Record<BucketKey, BucketData>;
		for (const key of VALID_BUCKET_KEYS) {
			result[key] = bucketMap.get(key) ?? DEFAULT_BUCKET;
		}

		return {
			...result,
			total: summary.totalContent,
		};
	}, [rawData]);

	return {
		data,
		loading,
		error,
	};
};
